import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDevFavicon } from '~/hooks/use-dev-favicon';

export function EnvironmentBanner(): React.ReactNode {
  if (import.meta.env.MODE === 'production') return null;
  return <BannerInner />;
}

function BannerInner(): React.ReactNode {
  const { t } = useTranslation();
  useDevFavicon();

  const [position, setPosition] = useState<{ x: number; y: number }>(() => ({
    x: window.innerWidth / 2,
    y: 16
  }));
  const isDragging = useRef(false);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    function onMouseMove(e: MouseEvent): void {
      if (!isDragging.current) return;
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y
      });
    }

    function onMouseUp(): void {
      isDragging.current = false;
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const isTest = import.meta.env.MODE === 'test';

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  }

  function onTouchStart(e: React.TouchEvent<HTMLDivElement>): void {
    const touch = e.touches[0];
    if (!touch) return;
    isDragging.current = true;
    dragOffset.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    };
  }

  function onTouchMove(e: React.TouchEvent<HTMLDivElement>): void {
    if (!isDragging.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    setPosition({
      x: touch.clientX - dragOffset.current.x,
      y: touch.clientY - dragOffset.current.y
    });
  }

  function onTouchEnd(): void {
    isDragging.current = false;
  }

  return (
    <div
      className={`fixed z-50 -translate-x-1/2 cursor-grab select-none rounded-full px-4 py-1.5 text-xs font-semibold shadow-md active:cursor-grabbing ${
        isTest ? 'bg-blue-500 text-white' : 'bg-amber-400 text-amber-950'
      }`}
      style={{ left: position.x, top: position.y }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {isTest ? t('environmentBanner.test') : t('environmentBanner.development')}
    </div>
  );
}
